import {Component, signal, inject, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {MaximumWorkingHours} from '../../../../classes/models/employee/MaximumWorkingHours';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {
  EmployeeMaximumWorkingHoursService
} from '../../../../services/http/employee/employee-maximum-working-hours.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Data required to open the delete maximum working hours modal. */
export interface MaxWorkingHoursDeleteData {
  employeePositionId?: EmployeePositionId;
  maximumWorkingHours: MaximumWorkingHours;
}

/**
 * This component handles deleting maximum working hours setting of an employee.
 */
@Component({
  selector: 'app-max-working-hours-delete',
  templateUrl: './max-working-hours-delete.component.html',
  styleUrls: ['./max-working-hours-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class MaxWorkingHoursDeleteComponent {
  private employeeMaximumWorkingHoursService = inject(EmployeeMaximumWorkingHoursService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined);
  /** Maximum working hours setting to be deleted. */
  readonly maximumWorkingHours = signal<MaximumWorkingHours>(undefined);

  /** `True` if request to backend is in progress. */
  loading = signal(false);

  static open(modalService: NgbModal, data: MaxWorkingHoursDeleteData): NgbModalRef {
    const modalRef = modalService.open(MaxWorkingHoursDeleteComponent);
    const instance = modalRef.componentInstance as MaxWorkingHoursDeleteComponent;
    instance.employeePositionId.set(data.employeePositionId);
    instance.maximumWorkingHours.set(data.maximumWorkingHours);
    return modalRef;
  }

  /**
   * Sends a request to backend to delete maximum working hours setting.
   * @param modalComponent a modal component of deleting maximum working hours.
   */
  confirm(modalComponent: ModalComponent): void {
    this.loading.set(true);

    this.employeeMaximumWorkingHoursService
            .deleteMaximumWorkingHours(this.employeePositionId(), this.maximumWorkingHours().startDate)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Maximum working hours successfully deleted');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}